/**
 * Postal-code + phone-prefix reference per country, ported from v1's Countries table. Drives the
 * country-aware zip validation and the phone-prefix selector on the Details step. `Regex` is a
 * postal-code pattern (as a string, compiled on use); `Format` is the human-readable shape shown in
 * the validation hint; `PhonePrefix` is the international dialling code (no `+`).
 */
export interface Country {
  Country: string
  ISO: string
  Format: string
  Regex: string
  PhonePrefix: string
}

export const Countries: Country[] = [
  { Country: 'Austria', ISO: 'AT', Format: '0000', Regex: '^\\d{4}$', PhonePrefix: '43' },
  { Country: 'Belgium', ISO: 'BE', Format: '0000', Regex: '^\\d{4}$', PhonePrefix: '32' },
  { Country: 'Czech Republic', ISO: 'CZ', Format: '000 00', Regex: '^(([0-9]{5})|([0-9]{3}[ ]{0,1}[0-9]{2}))$', PhonePrefix: '420' },
  { Country: 'Denmark', ISO: 'DK', Format: '0000', Regex: '^\\d{4}$', PhonePrefix: '45' },
  { Country: 'Germany', ISO: 'DE', Format: '00000', Regex: '^\\d{5}$', PhonePrefix: '49' },
  { Country: 'France', ISO: 'FR', Format: '00000', Regex: '^(?:0[1-9]|[1-8]\\d|9[0-8])\\d{3}$', PhonePrefix: '33' },
  { Country: 'Spain', ISO: 'ES', Format: '00000', Regex: '^\\d{5}$', PhonePrefix: '34' },
  { Country: 'United Kingdom', ISO: 'GB', Format: '**** ***', Regex: '^([Gg][Ii][Rr] 0[Aa]{2})|((([A-Za-z][0-9]{1,2})|(([A-Za-z][A-Ha-hJ-Yj-y][0-9]{1,2})|(([A-Za-z][0-9][A-Za-z])|([A-Za-z][A-Ha-hJ-Yj-y][0-9][A-Za-z]?))))\\s?[0-9][A-Za-z]{2})$', PhonePrefix: '44' },
  { Country: 'Ireland', ISO: 'IE', Format: 'a00 ****', Regex: '^([AC-FHKNPRTV-Y]{1}[0-9]{2}|D6W)[ ]?[0-9AC-FHKNPRTV-Y]{4}$', PhonePrefix: '353' },
  { Country: 'Netherlands', ISO: 'NL', Format: '0000 aa', Regex: '^(?:NL-)?(\\d{4})\\s*([A-Z]{2})$', PhonePrefix: '31' },
  { Country: 'Poland', ISO: 'PL', Format: '00-000', Regex: '^[0-9]{2}[-]([0-9]){3}$', PhonePrefix: '48' },
  { Country: 'Romania', ISO: 'RO', Format: '000000', Regex: '^\\d{6}$', PhonePrefix: '40' },
  { Country: 'Sweden', ISO: 'SE', Format: '000 00', Regex: '^[0-9]{3}\\s?[0-9]{2}$', PhonePrefix: '46' },
  { Country: 'Slovenia', ISO: 'SI', Format: '0000', Regex: '^\\d{4}$', PhonePrefix: '386' },
  { Country: 'Slovakia', ISO: 'SK', Format: '00000', Regex: '^\\d{5}$', PhonePrefix: '421' },
  { Country: 'Switzerland', ISO: 'CH', Format: '0000', Regex: '^[1-9]\\d{3}$', PhonePrefix: '41' },
  { Country: 'Italy', ISO: 'IT', Format: '00000', Regex: '^\\d{5}$', PhonePrefix: '39' },
  { Country: 'Norway', ISO: 'NO', Format: '0000', Regex: '^\\d{4}$', PhonePrefix: '47' },
  { Country: 'Finland', ISO: 'FI', Format: '00000', Regex: '^\\d{5}$', PhonePrefix: '358' },
  { Country: 'Bulgaria', ISO: 'BG', Format: '0000', Regex: '^\\d{4}$', PhonePrefix: '359' },
  { Country: 'Croatia', ISO: 'HR', Format: '00000', Regex: '^\\d{5}$', PhonePrefix: '385' },
  { Country: 'Cyprus', ISO: 'CY', Format: '0000', Regex: '^\\d{4}$', PhonePrefix: '357' },
  { Country: 'Estonia', ISO: 'EE', Format: '00000', Regex: '^\\d{5}$', PhonePrefix: '372' },
  { Country: 'Greece', ISO: 'GR', Format: '000 00', Regex: '^\\d{3}[ ]?\\d{2}$', PhonePrefix: '30' },
  { Country: 'Hungary', ISO: 'HU', Format: '0000', Regex: '^\\d{4}$', PhonePrefix: '36' },
  { Country: 'Iceland', ISO: 'IS', Format: '000', Regex: '^\\d{3}$', PhonePrefix: '354' },
  { Country: 'Latvia', ISO: 'LV', Format: '0000', Regex: '^\\d{4}$', PhonePrefix: '371' },
  { Country: 'Lithuania', ISO: 'LT', Format: '00000', Regex: '^\\d{5}$', PhonePrefix: '370' },
  { Country: 'Luxembourg', ISO: 'LU', Format: '0000', Regex: '^\\d{4}$', PhonePrefix: '352' },
  { Country: 'Malta', ISO: 'MT', Format: 'aaa 0000', Regex: '^[A-Z]{3}[ ]?\\d{4}$', PhonePrefix: '356' },
  { Country: 'Moldova', ISO: 'MD', Format: '0000', Regex: '^\\d{4}$', PhonePrefix: '373' },
  { Country: 'Monaco', ISO: 'MC', Format: '98000', Regex: '^980\\d{2}$', PhonePrefix: '377' },
  { Country: 'Montenegro', ISO: 'ME', Format: '00000', Regex: '^\\d{5}$', PhonePrefix: '382' },
  { Country: 'North Macedonia', ISO: 'MK', Format: '0000', Regex: '^\\d{4}$', PhonePrefix: '389' },
  { Country: 'Portugal', ISO: 'PT', Format: '0000-000', Regex: '^\\d{4}[-]?\\d{3}$', PhonePrefix: '351' },
  { Country: 'Serbia', ISO: 'RS', Format: '000000', Regex: '^\\d{5,6}$', PhonePrefix: '381' },
]

/** Look up a country row by ISO code (e.g. "DK"). */
export function countryByISO(iso?: string | null): Country | undefined {
  return iso ? Countries.find((c) => c.ISO === iso) : undefined
}
